import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { Interruttore } from '@/components/common/Interruttore';
import { Sheet } from '@/components/common/Sheet';
import type { Card } from '@/lib/qcp/protocol';
import { PlayingCard } from './PlayingCard';

/**
 * «IMPOSTAZIONI» del tavolo del Chinese Poker (fase 2; tavole 6 e 9 della scheda `2026-10-17-tavolo-premium-ofc.md`, I-761/I-763/I-765/I-766): UN foglio con DUE interruttori,
 * come `ImpostazioniTavolo` del burraco (#718): titolo e tasto di chiusura, una riga per impostazione (titolo, descrizione, interruttore).
 *  - «Quattro colori»: la preferenza del SOLO Chinese Poker, spenta di default, con sotto la striscia delle quattro carte;
 *  - «Vibrazione»: la preferenza del SOLO Chinese Poker, che parte dalla vibrazione del telefono.
 * ⛔ SOLO PRESENTAZIONE: i valori e le scelte arrivano dalla rotta (`useImpostazioniCp`), che li ricorda sul dispositivo. Qui non si legge e non si scrive niente.
 * In piedi e un `Sheet` (si registra da solo nella pila dei livelli: il tasto indietro di Telegram chiude il foglio e non arriva all'uscita dal tavolo); nel palco
 * orizzontale e un PANNELLO a destra (tavola 9), perche un foglio fuori dal palco non girerebbe con lui.
 */
export interface ImpostazioniCpProps {
  quattroColori: boolean;
  onQuattroColori: (acceso: boolean) => void;
  vibrazione: boolean;
  onVibrazione: (acceso: boolean) => void;
  onClose: () => void;
}

const ANTEPRIMA: Card[] = [
  { r: 14, s: 's' },
  { r: 14, s: 'h' },
  { r: 14, s: 'd' },
  { r: 14, s: 'c' },
];

export function ImpostazioniContenuto({ quattroColori, onQuattroColori, vibrazione, onVibrazione, onClose, palco = false }: ImpostazioniCpProps & { palco?: boolean }) {
  const { t } = useTranslation('chinesepoker');
  const idColori = useId();
  const idVibra = useId();
  return (
    <div data-impostazioni-tavolo className={palco ? 'flex h-full flex-col gap-3 px-6 pb-4 pt-5' : 'flex flex-col gap-3 px-4 pb-4 pt-1'}>
      <header className="flex items-center gap-2">
        <h2 className="flex-1 text-base font-semibold text-fg">{t('impostazioni.titolo')}</h2>
        <Button variant="icona" size={32} tono="pieno" data-azione="chiudi-impostazioni" aria-label={t('impostazioni.chiudi')} onClick={onClose}>
          <X aria-hidden size={16} />
        </Button>
      </header>
      <div className="flex flex-col gap-2 border-t border-border">
        <div className="flex min-h-[54px] items-center gap-3">
          <div className="flex flex-1 flex-col gap-0.5 py-1.5">
            <span id={idColori} className="text-sm font-semibold text-fg">
              {t('impostazioni.quattroColori')}
            </span>
            <span className="text-xs leading-snug text-fg-muted">{t('impostazioni.quattroColoriDescr')}</span>
          </div>
          <Interruttore acceso={quattroColori} aria-labelledby={idColori} data-impostazione="quattroColori" onClick={() => onQuattroColori(!quattroColori)} />
        </div>
        <div data-anteprima-colori className="flex gap-2 pb-2">
          {ANTEPRIMA.map((c) => (
            <PlayingCard key={c.s} card={c} taglia="avv" quattroColori />
          ))}
        </div>
      </div>
      <div className="flex min-h-[54px] items-center gap-3 border-t border-border">
        <div className="flex flex-1 flex-col gap-0.5 py-1.5">
          <span id={idVibra} className="text-sm font-semibold text-fg">
            {t('impostazioni.vibrazione')}
          </span>
          <span className="text-xs leading-snug text-fg-muted">{t('impostazioni.vibrazioneDescr')}</span>
        </div>
        <Interruttore acceso={vibrazione} aria-labelledby={idVibra} data-impostazione="vibrazione" onClick={() => onVibrazione(!vibrazione)} />
      </div>
      <p className={`border-t border-border pt-3 text-xs leading-snug text-fg-muted ${palco ? 'mt-auto' : ''}`}>{t('impostazioni.nota')}</p>
    </div>
  );
}

export function CpImpostazioniFoglio({ open, ...resto }: ImpostazioniCpProps & { open: boolean }) {
  const { t } = useTranslation('chinesepoker');
  return (
    <Sheet open={open} onClose={resto.onClose} label={t('impostazioni.titolo')}>
      <ImpostazioniContenuto {...resto} />
    </Sheet>
  );
}
